import { useCallback, useEffect, useMemo, useState } from "react";
import { Collapse, Button, Drawer, Badge } from "antd";
import { cancelIcon, chevronClose, chevronOpen } from "svgs";
import { FilterOutlined, CheckOutlined } from "@ant-design/icons";

import { Checkbox } from "components/base";
import { CheckboxPanelList, RadioPanelList } from "./sub-components";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import {
  onChangeTripsFilters,
  onChangeRootTripsFilters,
  onChangeTimeRangeTripsFilters,
} from "store/actions";
import { getIndeterminate, getAllCheckedStatus } from "utils/helpers";
import { useLocation } from "react-router-dom";

import "styles/components/tab-filters/trips-filter.scss";

export default function TripsFilter() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  const {
    trips: { filters },
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);

  const {
    original: {
      Client: originalClient = [],
      "Time Range": originalTimeRange = [],
    },
    updated: {
      Client: updatedClient = [],
      "Time Range": updatedTimeRange = [],
    },
  } = filters;

  const { "Time Range": timeRangeUpdated, ...restUpdated } = filters.updated;
  const { "Time Range": timeRangeOriginal, ...restOriginal } = filters.original;

  // Remove "Client" filter for non-type-2 users
  const visibleFilters = useMemo(() => {
    if (userType === 2) return filters.original;
    return Object.fromEntries(
      Object.entries(filters.original).filter(([key]) => key !== "Client")
    );
  }, [filters.original, userType]);

  const originalClientWithTag = useMemo(
    () =>
      originalClient.map((client) => ({
        ...client,
        render: <></>,
      })),
    [originalClient]
  );

  const isAllChecked = useMemo(
    () => getAllCheckedStatus(restUpdated, restOriginal),
    [restUpdated, restOriginal]
  );

  const isIndeterminate = useMemo(
    () => getIndeterminate(restUpdated, restOriginal),
    [restUpdated, restOriginal]
  );

  // Count active filters for mobile badge
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    // Count time range if selected
    if (updatedTimeRange.length > 0 && updatedTimeRange[0]?.value) {
      count += 1;
    }
    // Count client filters
    if (userType === 2) {
      count += updatedClient.length;
    }
    return count;
  }, [updatedTimeRange, updatedClient, userType]);

  // Handle responsive detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
      if (window.innerWidth >= 992) {
        setMobileDrawerOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onChange = useCallback(
    (name: string, value: any, type: "list" | "all") => {
      dispatch(onChangeTripsFilters(name, value, type));
    },
    [dispatch]
  );

  const onRootChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(onChangeRootTripsFilters(event.target.checked));
    },
    [dispatch]
  );

  const onTimeRangeChange = useCallback(
    (value: any, range: any) => {
      dispatch(onChangeTimeRangeTripsFilters(value, range));
    },
    [dispatch]
  );

  const onClearAll = useCallback(() => {
    dispatch(onChangeRootTripsFilters(false));
  }, [dispatch]);

  const onCheckboxListChange = useCallback(
    (name: string, value: any, type: "list" | "all") => {
      dispatch(onChangeTripsFilters(name, value, type));
    },
    [dispatch]
  );

  const handleOpenDrawer = useCallback(() => {
    setMobileDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  useEffect(() => {
    dispatch(onChangeRootTripsFilters(false));
  }, [location.pathname, dispatch]);

  // Filter content - reused in both desktop and mobile
  const filterContent = (
    <Collapse
      className="__common-collapse"
      ghost
      expandIconPosition="right"
      expandIcon={({ isActive }) => (isActive ? chevronOpen : chevronClose)}
      defaultActiveKey={Object.keys(visibleFilters)}
    >
      <RadioPanelList
        header=""
        option={updatedTimeRange[0]}
        key="Time Range"
        name="Time Range"
        options={originalTimeRange}
        value={updatedTimeRange[0]?.value ?? ""}
        onChange={onTimeRangeChange}
      />
      {userType === 2 && (
        <CheckboxPanelList
          header=""
          key="Client"
          name="Client"
          options={originalClientWithTag}
          value={updatedClient.map((row: any) => row.value)}
          onChange={onCheckboxListChange}
          search
        />
      )}
    </Collapse>
  );

  return (
    <>
      {/* Desktop Filter - Same as Before */}
      <div className="common-filter trips-filter-desktop">
        <div className="__filter-header">
          {" "}
          <div className="all-options">
            <span className="filter-text">Filters</span>
            {userType === 2 && (
              <Button
                className="clear-all-btn"
                icon={cancelIcon}
                type="link"
                danger
                onClick={onClearAll}
              >
                Clear All
              </Button>
            )}
          </div>
        </div>
        {filterContent}
      </div>

      {/* Mobile Floating Action Button */}
      {isMobile && (
        <div className="mobile-filter-trigger">
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={handleOpenDrawer}
            className="filter-fab-btn"
          >
            <span className="fab-text">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="fab-badge">{activeFiltersCount}</span>
            )}
          </Button>
        </div>
      )}

      {/* Mobile Filter Drawer */}
      <Drawer
        title={
          <div className="drawer-title">
            <span>Filters</span>
            {activeFiltersCount > 0 && (
              <Badge count={activeFiltersCount} className="title-badge" />
            )}
          </div>
        }
        placement="right"
        onClose={handleCloseDrawer}
        visible={mobileDrawerOpen}
        className="trips-filter-drawer"
        width={300}
        bodyStyle={{ padding: 0 }}
        footer={
          <div className="drawer-footer">
            {userType === 2 && (
              <Button onClick={onClearAll} className="clear-btn">
                Clear All
              </Button>
            )}
            <Button
              type="primary"
              onClick={handleApplyFilters}
              icon={<CheckOutlined />}
              className={`apply-btn ${userType !== 2 ? "full-width" : ""}`}
            >
              Apply Filters
            </Button>
          </div>
        }
      >
        <div className="drawer-filter-content">{filterContent}</div>
      </Drawer>
    </>
  );
}